import { useCallback } from 'react';
import { GridCellSelectionModel } from '@mui/x-data-grid-premium';

export interface ClipboardConfig {
  cellSelectionModel: GridCellSelectionModel;
  onActionResult?: (result: { type: "error" | "info" | "success" | "warning"; message: string }) => void;
}

export function useClipboardWithEmptyCells(config: ClipboardConfig) {
  const {
    cellSelectionModel,
    onActionResult
  } = config;

  // Custom function to split pasted text while preserving empty cells
  const splitClipboardPastedText = useCallback((text: string) => {
    // Split by newlines to get rows
    const rows = text.split('\n');
    
    // Split each row by tabs and preserve empty cells
    return rows.map(row => {
      // Split by tab character, but preserve empty strings
      const cells = row.split('\t');
      
      // Ensure we don't lose trailing empty cells by checking if the row ends with tabs
      // Count trailing tabs to preserve empty cells at the end
      const trailingTabs = (row.match(/\t+$/)?.[0]?.length) || 0;
      
      // Add empty strings for trailing tabs that might have been lost
      for (let i = 0; i < trailingTabs; i++) {
        if (cells[cells.length - 1] !== '') {
          cells.push('');
        }
      }
      
      // Return strings only - the pastedValueParser will handle empty string to null conversion
      return cells.map(cell => cell || ''); // Convert any null/undefined to empty string
    });
  }, []);

  // Handle clipboard paste events with better empty cell support
  const onClipboardPasteStart = useCallback(() => {
    // We can add any pre-paste logic here if needed
    console.log('Clipboard paste started');
  }, []);

  const onClipboardPasteEnd = useCallback(() => {
    if (onActionResult) {
      onActionResult({
        type: "info",
        message: "Cells pasted successfully"
      });
    }
  }, [onActionResult]);

  // Custom function to handle the paste confirmation
  const onBeforeClipboardPasteStart = useCallback(async () => {
    // We can add validation logic here if needed
    // For now, just allow all paste operations
    return true;
  }, []);

  // Get the selected cell range to help with copy operations
  const getSelectedCellRange = useCallback(() => {
    if (Object.keys(cellSelectionModel).length === 0) return null;

    const selectedCells: { rowId: string; fieldName: string }[] = [];
    
    Object.entries(cellSelectionModel).forEach(([rowId, selectedFields]) => {
      if (selectedFields) {
        Object.keys(selectedFields).forEach(fieldName => {
          selectedCells.push({ rowId, fieldName });
        });
      }
    });

    if (selectedCells.length === 0) return null;

    // Find the bounds of the selection
    const rowIds = Array.from(new Set(selectedCells.map(cell => cell.rowId)));
    const fieldNames = Array.from(new Set(selectedCells.map(cell => cell.fieldName)));
    
    // Sort to get the range
    const sortedRowIds = rowIds.sort();
    const sortedFieldNames = fieldNames.sort();

    return {
      startRowId: sortedRowIds[0],
      endRowId: sortedRowIds[sortedRowIds.length - 1],
      startField: sortedFieldNames[0],
      endField: sortedFieldNames[sortedFieldNames.length - 1],
      selectedCells,
      rowIds: sortedRowIds,
      fieldNames: sortedFieldNames
    };
  }, [cellSelectionModel]);

  // Enhanced clipboard export behavior
  const clipboardCopyOptions = {
    // Use tab as delimiter (default)
    clipboardCopyCellDelimiter: '\t',
    
    // Use formatted values for copy to ensure single select labels are copied correctly
    // This allows single select values to be copied as their display labels
    ignoreValueFormatterDuringExport: {
      clipboardExport: false // Use formatted values including single select labels
    }
  };

  return {
    splitClipboardPastedText,
    onClipboardPasteStart,
    onClipboardPasteEnd,
    onBeforeClipboardPasteStart,
    getSelectedCellRange,
    clipboardCopyOptions
  };
}
